import React, { useState, useEffect, useRef } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    List,
    ListItem,
    FormControl,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useQuery, useMutation } from "@apollo/client";  
import { useCalendar } from "./calendarContext";  
import { GET_ALL_COUNTRIES } from "../../../../../platform/graphql/queries/countryStateCity";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import { GET_HOLIDAYS_BY_COUNTRY_AND_MONTH } from "../../../../../platform/graphql/queries/holiday";
import { ADD_BUSINESS_HOLIDAY, REMOVE_BUSINESS_HOLIDAY } from "../../../../../platform/graphql/mutations/businessHoliday";

const ALL_CATEGORIES = ["national", "religious", "cultural", "custom"];
const DEFAULT_CATEGORY = "national";
const SELECTION_STORAGE_PREFIX = "timely.geoCalendarSelection.v1";

const getSelectionStorageKey = (businessId) =>
    `${SELECTION_STORAGE_PREFIX}:${businessId}`;

const readSavedSelection = (businessId) => {
    try {
        const saved = JSON.parse(
            localStorage.getItem(getSelectionStorageKey(businessId)) || "null"
        );
        if (!saved || typeof saved !== "object") return null;

        const countries = Array.isArray(saved.countries)
            ? saved.countries.map(String).filter(Boolean).slice(0, 2)
            : [];
        const categories = Array.isArray(saved.categories)
            ? saved.categories.filter((category) => ALL_CATEGORIES.includes(category))
            : [];
        const positions = Object.fromEntries(
            Object.entries(saved.positions || {}).filter(
                ([countryId, position]) =>
                    countries.includes(String(countryId)) &&
                    (position === "left" || position === "right")
            )
        );

        return { countries, categories, positions };
    } catch {
        return null;
    }
};

function CalendarManagement() {
    const { setHolidayDatas, currentMonth } = useCalendar();

    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([DEFAULT_CATEGORY]);
    const [existingHolidays, setExistingHolidays] = useState([]);  // Default holidays
    const [message, setMessage] = useState("");
    const [holidaysByCountry, setHolidaysByCountry] = useState({});
    const [filteredHolidays, setFilteredHolidays] = useState({});
    const [positions, setPositions] = useState({}); // Tracks left/right positions for countries
    const [restoredBusinessId, setRestoredBusinessId] = useState(null);

    const messageRef = useRef(null);
    let messageTimer = useRef(null);

    const selectedBusiness = useSelectedBusiness();
    const businessId = Number(selectedBusiness?.businessBasic?.id);

    useEffect(() => {
        if (!Number.isFinite(businessId) || businessId <= 0) return;

        const saved = readSavedSelection(businessId);
        setSelectedCountries(saved?.countries || []);
        setSelectedCategories(saved?.categories || []);
        setPositions(saved?.positions || {});
        setRestoredBusinessId(businessId);
    }, [businessId]);

    useEffect(() => {
        if (restoredBusinessId !== businessId) return;

        try {
            localStorage.setItem(
                getSelectionStorageKey(businessId),
                JSON.stringify({
                    countries: selectedCountries,
                    categories: selectedCategories,
                    positions,
                })
            );
        } catch {
            // Calendar selection persistence is optional when browser storage is unavailable.
        }
    }, [businessId, positions, restoredBusinessId, selectedCategories, selectedCountries]);

    // Fetching all countries
    const { data: countriesData, loading: countriesLoading, error: countriesError } = useQuery(GET_ALL_COUNTRIES);

    // Fetch holidays for the selected countries
    const { data: holidaysByCountryData, loading: holidaysByCountryLoading } = useQuery(
        GET_HOLIDAYS_BY_COUNTRY_AND_MONTH,
        {
            variables: { countryIds: selectedCountries.map((id) => parseInt(id)), currentMonth: currentMonth },
            skip: selectedCountries.length === 0,
        }
    );


    const [addBusinessHoliday] = useMutation(ADD_BUSINESS_HOLIDAY);
    const [removeBusinessHoliday] = useMutation(REMOVE_BUSINESS_HOLIDAY);

    const handleCountryChange = (event) => {
        const { value, checked } = event.target;

        if (checked && selectedCountries.length >= 2) {
            setMessage(`You can select a maximum of two countries.`);
            clearTimeout(messageTimer.current);
            messageTimer.current = setTimeout(() => setMessage(""), 3000);
            return;
        }

        const updatedCountries = checked
            ? [...selectedCountries, value]
            : selectedCountries.filter((countryId) => countryId !== value);

        setSelectedCountries(updatedCountries);

        // Assign default positions if two countries are selected
        if (updatedCountries.length === 2) {
            setPositions({ [updatedCountries[0]]: "left", [updatedCountries[1]]: "right" });
        } else if (updatedCountries.length === 1) {
            setPositions({ [updatedCountries[0]]: "left" });
        } else {
            setPositions({});
        }
        setMessage("");
    };

    const handleCategoryChange = (event) => {
        const { value, checked } = event.target;

        setSelectedCategories((prev) =>
            checked ? [...prev, value] : prev.filter((category) => category !== value)
        );
    };

    const handleHolidayChange = async (holidayId, isChecked) => {
        try {
            if (isChecked) {
                await addBusinessHoliday({ variables: { businessId, holidayId } });
                setExistingHolidays((prev) => [...prev, holidayId.toString()]);
            } else {
                await removeBusinessHoliday({ variables: { businessId, holidayId } });
                setExistingHolidays((prev) =>
                    prev.filter((id) => id !== holidayId.toString())
                );
            }
        } catch (error) {
            setMessage(`Error updating holiday: ${error.message}`);
        }
    };

    const mergeHolidaysIntoBookingDatas = () => {
        let allHolidays = [];

        // Collect all holidays for the selected countries
        selectedCountries.forEach((countryId) => {
            const holidaysForCountry = filteredHolidays[countryId] || [];
            allHolidays = [...allHolidays, ...holidaysForCountry];
        }); 
        // Transform collected holidays to match the bookingDatas structure
        const transformedHolidays = allHolidays.map((holiday) => {
        // Ensure holiday is converted into a Date object if necessary
        // let holidayDate;
        // if (typeof holiday === "string" || typeof holiday.holidayDate === "string") {
        //     // Assuming holiday is a string or has a `date` string property
        //     holidayDate = new Date(holiday.holidayDate || holiday); // Parse the date
        // } else if (holiday instanceof Date) {
        //     // Already a Date object
        //     holidayDate = holiday;
        // } else if (holiday.year && holiday.month && holiday.day) {
        //     // Assuming holiday has year, month, day properties
        //     holidayDate = new Date(holiday.year, holiday.month - 1, holiday.day); // Create Date object
        // } else {
        //     throw new Error("Invalid holiday format");
        // }

        //EthiopianCalendar.getEthiopianDate(
        // holidayDate.getFullYear(),
        // holidayDate.getMonth() + 1, // Adjust for 0-based month
        // holidayDate.getDate()

            return {
                timezone: null,
                bookingId: null,
                finalPrice: null,
                discount: null,
                time: holiday.holidayDate, // Map holidayDate to time
                createdAt: holiday.createdAt, // Use holiday's createdAt
                status: "Holiday", // Custom status for holidays
                title: holiday.holidayName, // Map holidayName to inviteeName
                inviteeEmail: null,
                bookingTypeName: "Holiday", // Assign "Holiday" as booking type name
                staffName: null,
                isAllDay: true,
                color: holiday.color, 
                icon: holiday.icon, 
            };
        });

        // Keep holidays separate so later booking refreshes cannot overwrite them.
        setHolidayDatas(transformedHolidays);
    };

    useEffect(() => {
        const holidaysByCountryAndMonth = holidaysByCountryData?.getHolidaysByCountryAndMonth
        if (holidaysByCountryAndMonth) {
            const holidaysMap = {};
            holidaysByCountryAndMonth.forEach((holiday) => {
                const countryId = holiday.countryId ?? "unknown";

                if (!holidaysMap[countryId]) {
                    holidaysMap[countryId] = [];
                }

                holidaysMap[countryId].push(holiday);
            });

            setHolidaysByCountry(holidaysMap);

            // Populate existingHolidays with all holidays by default
            const allHolidayIds = holidaysByCountryAndMonth?.map((holiday) => holiday.id.toString());
            setExistingHolidays(allHolidayIds);
        } else {
            setHolidaysByCountry({});
        }
    }, [holidaysByCountryData]);

    useEffect(() => {
        const filteredHolidaysMap = {};

        Object.keys(holidaysByCountry).forEach((countryId) => {
            const filteredHolidays = holidaysByCountry[countryId].filter((holiday) =>
                selectedCategories.some((category) =>
                    holiday.categories.includes(category)
                )
            );
            filteredHolidaysMap[countryId] = filteredHolidays;
        });

        setFilteredHolidays(filteredHolidaysMap); 
    }, [selectedCategories, holidaysByCountry]);

    useEffect(() => {
        if (restoredBusinessId !== businessId) return;

        if (selectedCountries.length === 0) {
            setSelectedCategories([]);
            setExistingHolidays([]);
            setHolidaysByCountry({});
            setFilteredHolidays({});
        }
        else if (selectedCategories.length === 0) {
            setSelectedCategories([DEFAULT_CATEGORY])
        }
    }, [businessId, restoredBusinessId, selectedCategories.length, selectedCountries]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (messageRef.current && !messageRef.current.contains(event.target)) {
                setMessage("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        mergeHolidaysIntoBookingDatas();
    }, [filteredHolidays]);

    if (countriesLoading) return <CircularProgress />;
    if (countriesError) return <p>Error loading countries: {countriesError.message}</p>; 

    const handlePositionChange = (countryId, newPosition) => {
        const otherCountry = selectedCountries.find((id) => id !== countryId);
        if (otherCountry) {
            setPositions({
                [countryId]: newPosition,
                [otherCountry]: newPosition === "left" ? "right" : "left",
            });
        }
    };

    return (
        <div style={{ width: "600px" }}>
            {/* Country Selection */}
            <div>
                <h7>Select Countries</h7>
                <List sx={{ maxHeight: 200, overflowY: "auto", paddingLeft: 0, pl: "15px" }}>
                    {countriesData?.getAllCountries?.map((country) => (
                        <ListItem key={country.id} sx={{ paddingLeft: 0, paddingTop: 0, paddingBottom: 0 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        value={country.id}
                                        checked={selectedCountries.includes(country.id.toString())}
                                        onChange={handleCountryChange}
                                    />
                                }
                                label={country.name}
                            />
                        </ListItem>
                    ))}
                </List>
                {message && (
                    <p style={{ color: "red" }} ref={messageRef}>
                        {message}
                    </p>
                )}
            </div>
            {selectedCountries.length === 2 && (
                <div style={{ marginTop: "20px" }}> 
                    {selectedCountries.length === 2 && (
                        <FormControl sx={{ marginBottom: "10px" }}>
                            <Typography>
                                Assign position for{" "}
                                {countriesData?.getAllCountries.find((c) => c.id === selectedCountries[0])?.name}
                            </Typography>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={positions[selectedCountries[0]] === "left"}
                                            onChange={() => {
                                                setPositions({
                                                    [selectedCountries[0]]: "left",
                                                    [selectedCountries[1]]: "right",
                                                });
                                            }}
                                        />
                                    }
                                    label="Left"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={positions[selectedCountries[0]] === "right"}
                                            onChange={() => {
                                                setPositions({
                                                    [selectedCountries[0]]: "right",
                                                    [selectedCountries[1]]: "left",
                                                });
                                            }}
                                        />
                                    }
                                    label="Right"
                                />
                            </div>
                        </FormControl>
                    )}

                </div>
            )}
            {/* Category Selection */}
            <div style={{ marginTop: "5px" }}>
                <h7>Categories</h7>
                <List sx={{ maxHeight: 150, overflowY: "auto", pl: "15px" }}>
                    {ALL_CATEGORIES.map((category) => (
                        <ListItem key={category} sx={{ paddingLeft: 0, paddingTop: 0, paddingBottom: 0 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        value={category}
                                        checked={selectedCategories.includes(category)}
                                        onChange={handleCategoryChange}
                                    />
                                }
                                label={category.charAt(0).toUpperCase() + category.slice(1)}
                                sx={{ margin: 0 }} // Remove margin from FormControlLabel to reduce spacing
                            />
                        </ListItem>
                    ))}
                </List>
            </div>

            {/* Display Holidays for Selected Countries */}
            <div style={{ marginTop: "35px" }}>
                {selectedCountries.map((countryId) => (
                    <div key={countryId}>
                        <h3>Holidays for {countriesData?.getAllCountries.find((c) => Number(c.id) === Number(countryId))?.name}</h3>
                        <List sx={{ paddingLeft: 0 }}>
                            {filteredHolidays[countryId]?.map((holiday) => (
                                <ListItem key={holiday.id} sx={{ paddingLeft: 0 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={existingHolidays.includes(holiday.id.toString())}
                                                onChange={(e) =>
                                                    handleHolidayChange(holiday.id, e.target.checked)
                                                }
                                            />
                                        }
                                        label={holiday.holidayName}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </div>
                ))}
            </div>

        </div>

    );
}

function GeoCalendar() {
    const [expanded, setExpanded] = useState(false);

    const handleAccordionToggle = () => {
        setExpanded(!expanded);
    };

    return (
        <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", mt: 0 }}>
            <Accordion
                expanded={expanded}
                onChange={handleAccordionToggle}
                sx={{
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:before": { display: "none" },
                    borderRadius: 1,
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        borderBottom: expanded ? "1px solid rgba(0,0,0,0.12)" : "none",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Geo Holiday's Calendar
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <CalendarManagement />
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}

export default GeoCalendar;
